import { ReactNode, createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

import { STROKE } from '../theme/tokens';

// FD-470 — the top bar's page-action slot.
//
// The AirBridge board puts a page's primary CTA in the top bar, past the
// environment selector — "+ Create Group" on the groups screen. The top bar is
// shell chrome and the action belongs to the page, so something has to carry a
// node across that boundary.
//
// IT IS A PORTAL, NOT A REGISTRATION, AND THAT IS THE WHOLE DESIGN.
//
// The obvious shape — a context holding a ReactNode, set by the page in an
// effect — has two failure modes that are easy to write and hard to see. A
// `children` prop is a new object on every render, so an effect that stores it
// sets state every render and spins; and an effect that stores a node must
// clear it on unmount, or the action belonging to the page an operator just
// left survives into the next route. A portal has neither problem BY
// CONSTRUCTION: nothing is stored, so nothing can go stale, and React unmounts
// the portalled children with the page that owns them. The cleanup is not a
// rule someone has to remember.
//
// The only state here is the slot ELEMENT, set once by a callback ref when the
// top bar mounts. That is a stable DOM node, so it settles after one extra
// render and never changes again.
//
// This carries CHROME, never data. Nothing reads the slot back, and a page must
// not use it to hand state to the shell — the URL is where page state lives
// (ADR-015, restated by FD-ADR-031 §1 and FD-ADR-043).

interface PageActionsValue {
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
}

const PageActionsContext = createContext<PageActionsValue | null>(null);

export function PageActionsProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  // Not memoised deliberately: `slot` changes exactly once, on mount, so a
  // useMemo here would guard against a re-render that cannot happen.
  return (
    <PageActionsContext.Provider value={{ slot, setSlot }}>{children}</PageActionsContext.Provider>
  );
}

/**
 * The receiving end, rendered by `TopBar`.
 *
 * Always renders its container — the callback ref is how the provider learns
 * where to portal to, so a slot that rendered only when actions existed could
 * never receive any. The divider is drawn by the container and is invisible
 * while the slot is empty, because an empty flex row has no width.
 */
export function PageActionsSlot() {
  const ctx = useContext(PageActionsContext);
  return (
    <div
      data-testid="page-actions-slot"
      className="flex items-center gap-2 empty:hidden"
      style={{ borderLeft: `1px solid ${STROKE.base}`, paddingLeft: '16px', marginLeft: '4px' }}
      ref={ctx?.setSlot ?? null}
    />
  );
}

/**
 * The sending end. A page renders this anywhere in its tree; its children
 * appear in the top bar.
 *
 * Renders nothing and portals nothing when there is no provider — a page under
 * test, or rendered outside the shell, behaves exactly as it did before.
 */
export function PageActions({ children }: { children: ReactNode }) {
  const ctx = useContext(PageActionsContext);
  if (!ctx?.slot) return null;
  return createPortal(children, ctx.slot);
}
